/**
 * Function to calculate the overlap percentage of 'wgt' between tickers that exist in multiple parents (ETFs).
 *
 * @param {Array} data - The flat array of objects containing 'parent', 'ticker', and 'wgt' fields.
 * @returns {Array} - Array of objects representing the overlap percentage between pairs of parents.
 *                    Each object contains 'x' (parent1), 'y' (parent2), and 'value' (overlap percentage).
 */
function calculateOverlap(data) {
    // Step 1: Create a map to store tickers with their corresponding parents and weights
    const tickerMap = new Map();

    data.forEach(item => {
        if (!tickerMap.has(item.ticker)) {
            tickerMap.set(item.ticker, []);
        }
        tickerMap.get(item.ticker).push({ parent: item.parent, wgt: item.wgt });
    });

    // Step 2: Calculate overlap percentage for each pair of parents
    const overlapResults = [];
    const parentPairs = new Map();

    tickerMap.forEach((parentWgts, ticker) => {
        for (let i = 0; i < parentWgts.length; i++) {
            for (let j = i + 1; j < parentWgts.length; j++) {
                const parent1 = parentWgts[i].parent;
                const parent2 = parentWgts[j].parent;
                const wgt1 = parentWgts[i].wgt;
                const wgt2 = parentWgts[j].wgt;
                const pairKey = `${parent1}-${parent2}`;

                if (!parentPairs.has(pairKey)) {
                    parentPairs.set(pairKey, { totalWgt1: 0, totalWgt2: 0, overlapWgt: 0 });
                }

                const pairData = parentPairs.get(pairKey);
                pairData.totalWgt1 += wgt1;
                pairData.totalWgt2 += wgt2;
                pairData.overlapWgt += Math.min(wgt1, wgt2);
            }
        }
    });

    parentPairs.forEach((pairData, pairKey) => {
        const [parent1, parent2] = pairKey.split('-');
        const overlapPercentage = (pairData.overlapWgt / Math.min(pairData.totalWgt1, pairData.totalWgt2)) * 100;
        overlapResults.push({ x: parent1, y: parent2, value: overlapPercentage });
    });

    return overlapResults;
}


function flattenEtfPositions(etfPositions, weightMap = {}) {
  const totalWeight = Object.values(weightMap).reduce((sum, weight) => sum + weight, 0);
  const isWeightMapProvided = totalWeight > 0;

  return etfPositions.reduce((acc, etf) => {
    const etfWeight = isWeightMapProvided ? (weightMap[etf.symbol] || 0) : 1 / etfPositions.length;
    etf.holdings.forEach(holding => {
      acc.push({
        parent: etf.symbol,
        ...holding,
        symbol: holding.symbol,
        wgt: holding.wgt,
        scaledWeight: holding.wgt * etfWeight,
      });
    });
    return acc;
  }, []);
}

/**
 * Consolidate ETF positions by summing up the scaled weights of the same stock across different ETFs.
 * @param flattenedPositions - {Array} flattenEtfPositions output
 * @returns {*}
 */
function consolidateEtfPositions(flattenedPositions) {
    let cumWeight = 0;
    let consolidatedPositions = flattenedPositions.reduce((acc, item) => {
        // Find if the item's symbol already exists in the accumulator
        let existing = acc.find(stock => stock.symbol === item.symbol);
        if (existing) {
          // If the stock already exists, update the weighted weight and add the parent if it's not already included
          existing.scaledWeight += item.scaledWeight;
          if (!existing.parents.includes(item.parent)) {
            existing.parents.push(item.parent);
          }
        } else {
          // If the stock doesn't exist, create a new entry with the parents array
          acc.push({
            symbol: item.symbol,
              ...item,
            wgt: item.scaledWeight,
            parents: [item.parent],
          });
        }
        return acc;
        }, []);
    consolidatedPositions.forEach(stock => {
        cumWeight += stock.wgt;
        stock.cumsum = cumWeight;
    });
    consolidatedPositions.sort((a, b) => b.wgt - a.wgt);
    return consolidatedPositions;

}


/**
 *
 * @param consolidatedList {Array} - consolidateEtfPositions output
 * @param cutOff {number} - the cut off value
 * @returns {{outSet: *[], subSet: *[]}}
 */
export const cutConsolidatedList = (consolidatedList, cutOff) => {
    const subSet = [];
    const outSet = [];
    consolidatedList.forEach((stock) => {
        if (stock.cumsum < cutOff) {
            subSet.push(stock);
        } else {
            outSet.push(stock);
        }
    });
    return { subSet, outSet }
}



export { calculateOverlap, flattenEtfPositions, consolidateEtfPositions };